import { Controller } from "@hotwired/stimulus"

// Landing-page controller — handles:
//   • sticky nav mobile toggle + active-section highlight
//   • smooth scroll
//   • announcement banner dismiss
//   • pricing tab switching
//   • FAQ accordion
//   • scroll reveals + number counters via IntersectionObserver
//
// Honors prefers-reduced-motion throughout.
export default class extends Controller {
  static targets = [
    "banner",
    "mobileNav",
    "pricingTab",
    "pricingPanel",
    "navLink",
    "counter",
    "reveal",
    "faqItem",
    "lightbox",
    "lightboxImage",
    "proofCollage",
    "proofTemplate",
    "proofRevealWrap"
  ]

  static values = {
    revealed: { type: Boolean, default: false }
  }

  connect() {
    this.prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.checkBannerDismissed()
    this.setupScrollReveal()
    this.setupCounters()
    this.setupSectionSpy()
    this.setupLightboxKeyboard()
  }

  disconnect() {
    this.revealObserver?.disconnect()
    this.counterObserver?.disconnect()
    this.spyObserver?.disconnect()
    if (this.handleLightboxKeydown) {
      document.removeEventListener("keydown", this.handleLightboxKeydown)
    }
    // Reset overflow — if the user Turbo-navigates away while the lightbox
    // is open, we'd otherwise leave `overflow: hidden` on <body> and the
    // next page becomes unscrollable.
    document.body.style.overflow = ""
  }

  // ========== Proof-collage lightbox ==========

  openLightbox(event) {
    if (!this.hasLightboxTarget || !this.hasLightboxImageTarget) return
    const clickedImage = event.currentTarget.querySelector("img")
    if (!clickedImage) return
    this.lightboxImageTarget.src = clickedImage.src
    this.lightboxImageTarget.alt = clickedImage.alt || ""
    this.lightboxTarget.classList.add("is-open")
    document.body.style.overflow = "hidden"
  }

  closeLightbox(event) {
    if (event) event.stopPropagation()
    if (!this.hasLightboxTarget) return
    this.lightboxTarget.classList.remove("is-open")
    if (this.hasLightboxImageTarget) this.lightboxImageTarget.src = ""
    document.body.style.overflow = ""
  }

  // `error` does not bubble — data-action must be on the <img> itself, not a parent.
  hideBrokenProof(event) {
    const figure = event.currentTarget.closest(".landing-proof-collage__item")
    figure?.remove()
  }

  // Clones the hidden <template> of remaining proof tiles into the collage.
  // The template's contents are inert per-spec (no resource prefetch), so
  // until this click fires the user pays zero network cost for those 54
  // images. Once appended, each image's loading="lazy" still defers the
  // actual fetch until it scrolls into view.
  revealMoreProofs() {
    if (!this.hasProofCollageTarget || !this.hasProofTemplateTarget) return
    const fragment = this.proofTemplateTarget.content.cloneNode(true)
    this.proofCollageTarget.appendChild(fragment)
    this.proofTemplateTarget.remove()
    if (this.hasProofRevealWrapTarget) this.proofRevealWrapTarget.remove()
  }

  setupLightboxKeyboard() {
    if (!this.hasLightboxTarget) return
    this.handleLightboxKeydown = (e) => {
      if (e.key === "Escape" && this.lightboxTarget.classList.contains("is-open")) {
        this.closeLightbox()
      }
    }
    document.addEventListener("keydown", this.handleLightboxKeydown)
  }

  // ========== Banner ==========

  // localStorage access can throw SecurityError (read-blocked in locked-down
  // browsers) or QuotaExceededError (write-blocked in private/incognito mode).
  // Catch ONLY DOMException subclasses so programming bugs (ReferenceError,
  // TypeError) still surface instead of silently returning null.
  storageGet(key) {
    try { return localStorage.getItem(key) }
    catch (e) { if (e instanceof DOMException) return null; throw e }
  }

  storageSet(key, value) {
    try { localStorage.setItem(key, value) }
    catch (e) { if (!(e instanceof DOMException)) throw e }
  }

  storageRemove(key) {
    try { localStorage.removeItem(key) }
    catch (e) { if (!(e instanceof DOMException)) throw e }
  }

  checkBannerDismissed() {
    if (!this.hasBannerTarget) return
    // Migrate the pre-redesign key so returning visitors who dismissed the
    // old beta banner don't see the new announcement on their first visit.
    if (this.storageGet("beta-banner-dismissed") === "true") {
      this.storageSet("landing-announce-dismissed", "true")
      this.storageRemove("beta-banner-dismissed")
    }
    if (this.storageGet("landing-announce-dismissed") === "true") {
      this.bannerTarget.remove()
    }
  }

  dismissBanner(event) {
    event.preventDefault()
    if (!this.hasBannerTarget) return
    this.bannerTarget.remove()
    this.storageSet("landing-announce-dismissed", "true")
  }

  // ========== Mobile nav ==========

  toggleMobileNav(event) {
    event.preventDefault()
    if (!this.hasMobileNavTarget) return
    const open = this.mobileNavTarget.classList.toggle("is-open")
    // Sync ARIA state so screen readers announce the menu's state correctly
    // (WCAG 2.1 4.1.2). Matches the `aria-label` "Open menu" / "Close menu".
    const trigger = event.currentTarget
    trigger.setAttribute("aria-expanded", open ? "true" : "false")
    trigger.setAttribute("aria-label", open ? "Close menu" : "Open menu")
  }

  closeMobileNav() {
    if (!this.hasMobileNavTarget) return
    this.mobileNavTarget.classList.remove("is-open")
    const trigger = document.querySelector(".landing-header__mobile-toggle")
    if (trigger) {
      trigger.setAttribute("aria-expanded", "false")
      trigger.setAttribute("aria-label", "Open menu")
    }
  }

  // ========== Smooth scroll ==========

  scrollToSection(event) {
    event.preventDefault()
    const sectionId = event.currentTarget.dataset.section
    const behavior = this.prefersReducedMotion ? "auto" : "smooth"

    // "Home" scrolls to the true top of the page, above the hero. Using
    // element.scrollIntoView on #home would stop at the hero's top edge,
    // leaving the announcement banner and sticky header still covering
    // the viewport top — which reads as "not fully home".
    if (sectionId === "home") {
      window.scrollTo({ top: 0, behavior })
      this.closeMobileNav()
      return
    }

    const element = document.getElementById(sectionId)
    if (!element) {
      // Section doesn't exist on this page (e.g. user is on /welcome/terms
      // clicking "Pricing"). Navigate to the home page with the hash so the
      // browser anchor-scrolls after load.
      window.location.href = `/#${sectionId}`
      return
    }

    // Compensate for the sticky header so clicked sections aren't hidden
    // beneath it. Measure at click-time instead of hard-coding a value —
    // responsive breakpoints change the header height (40px logo mobile,
    // 48px desktop + padding), and this adapts automatically.
    const header = document.querySelector(".landing-header")
    const offset = header ? header.offsetHeight : 0
    const top = element.getBoundingClientRect().top + window.pageYOffset - offset
    window.scrollTo({ top, behavior })
    this.closeMobileNav()
  }

  // ========== Pricing tabs ==========

  switchPricingTab(event) {
    event.preventDefault()
    const target = event.currentTarget.dataset.pricingKey
    if (!target) return

    this.pricingTabTargets.forEach((tab) => {
      tab.classList.toggle("is-active", tab.dataset.pricingKey === target)
      tab.setAttribute("aria-selected", tab.dataset.pricingKey === target ? "true" : "false")
    })

    this.pricingPanelTargets.forEach((panel) => {
      panel.classList.toggle("is-active", panel.dataset.pricingKey === target)
    })
  }

  // ========== FAQ ==========

  toggleFaq(event) {
    event.preventDefault()
    const item = event.currentTarget.closest("[data-landing-page-target='faqItem']")
    if (!item) return
    const isOpen = item.classList.contains("is-open")
    // Close all items (single-open accordion) and sync aria-expanded on each
    // trigger so screen readers announce the collapsed state (WCAG 2.1 4.1.2).
    this.faqItemTargets.forEach((node) => {
      node.classList.remove("is-open")
      const btn = node.querySelector(".landing-faq-item__trigger, [aria-expanded]")
      if (btn) btn.setAttribute("aria-expanded", "false")
    })
    if (!isOpen) {
      item.classList.add("is-open")
      event.currentTarget.setAttribute("aria-expanded", "true")
    }
  }

  // ========== Scroll reveal ==========

  setupScrollReveal() {
    if (this.prefersReducedMotion || !("IntersectionObserver" in window)) {
      this.revealTargets.forEach((el) => el.classList.add("is-revealed"))
      return
    }

    this.revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed")
            this.revealObserver.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    )

    this.revealTargets.forEach((el) => this.revealObserver.observe(el))
  }

  // ========== Animated counters (re-triggers on re-scroll, debounced) ==========

  setupCounters() {
    if (!("IntersectionObserver" in window) || !this.hasCounterTarget) return

    // Per-element state: last-fired timestamp + active rAF handle.
    this.counterState = new WeakMap()

    this.counterObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return

          const state = this.counterState.get(entry.target) || {}
          const now = performance.now()
          // Debounce: only re-fire if ≥2s elapsed since the last fire.
          // Avoids "twitchy" re-tween when scroll-thrashing past the counter.
          if (state.lastFiredAt && now - state.lastFiredAt < 2000) return

          this.animateCounter(entry.target)
        })
      },
      // Keep observing after first fire (NO unobserve) so re-entry re-tweens.
      { threshold: 0.3 }
    )

    this.counterTargets.forEach((el) => this.counterObserver.observe(el))
  }

  animateCounter(el) {
    const target = parseFloat(el.dataset.counterTo || "0")
    const prefix = el.dataset.counterPrefix || ""
    const suffix = el.dataset.counterSuffix || ""
    const formatter = el.dataset.counterFormat || "int"

    if (this.prefersReducedMotion || !Number.isFinite(target)) {
      el.textContent = `${prefix}${this.formatCounter(target, formatter)}${suffix}`
      return
    }

    // Cancel any in-flight tween on this element so we don't double-animate
    // when the user scrolls back into view mid-animation.
    const state = this.counterState.get(el) || {}
    if (state.rafHandle) cancelAnimationFrame(state.rafHandle)

    const duration = 1400
    const start = performance.now()

    const step = (now) => {
      const progress = Math.min(1, (now - start) / duration)
      const eased = 1 - Math.pow(1 - progress, 3)
      const value = target * eased
      el.textContent = `${prefix}${this.formatCounter(value, formatter)}${suffix}`
      if (progress < 1) {
        const handle = requestAnimationFrame(step)
        this.counterState.set(el, { ...this.counterState.get(el), rafHandle: handle })
      } else {
        this.counterState.set(el, { lastFiredAt: performance.now(), rafHandle: null })
      }
    }

    const initialHandle = requestAnimationFrame(step)
    this.counterState.set(el, { lastFiredAt: start, rafHandle: initialHandle })
  }

  formatCounter(value, formatter) {
    if (formatter === "comma") {
      return Math.round(value).toLocaleString("en-US")
    }
    if (formatter === "decimal1") {
      return value.toFixed(1)
    }
    return Math.round(value).toString()
  }

  // ========== Section spy (sticky nav highlight) ==========

  setupSectionSpy() {
    if (!("IntersectionObserver" in window) || !this.hasNavLinkTarget) return

    const sectionIds = this.navLinkTargets
      .map((link) => link.dataset.section)
      .filter(Boolean)

    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean)

    if (!sections.length) return

    this.spyObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          const id = entry.target.id
          this.navLinkTargets.forEach((link) => {
            link.classList.toggle("is-active", link.dataset.section === id)
          })
        })
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
    )

    sections.forEach((section) => this.spyObserver.observe(section))
  }
}
